import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import { getLabelFromJspVariables } from '../common/JspUtil';
import * as actions from './actionCreators';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import { useJspVariables, useNavigation, useAuth, useLoader, useDebugRender, usePageTitle } from '../hooks';
import { IceIcon, PageContainer, PageHeader, UserMenu, DialogTitleWithClose } from '../components';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Skeleton from '@mui/material/Skeleton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const PasskeyTableSkeleton = () => (
    <TableBody>
        {[0, 1, 2].map(i => (
            <TableRow key={i}>
                <TableCell></TableCell>
                <TableCell><Skeleton variant="text" width={160} /></TableCell>
                <TableCell><Skeleton variant="text" width={100} /></TableCell>
                <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
            </TableRow>
        ))}
    </TableBody>
);
const PageWebAuthnDevices = () => {
    useDebugRender('PageWebAuthnDevices');
    usePageTitle('Registered Passkeys');
    const navigate = useNavigate();
    const { launcher, panelStyle } = useJspVariables();
    const { toPageWebAuthnRegister } = useNavigation();
    const { authenticatedUser, isAuthenticated, logout } = useAuth();
    const { showLoader, hideLoader } = useLoader();

    const [webAuthnDevices, setWebAuthnDevices] = useState(null);
    const [alert, setAlert] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(constants.ROUTE_PAGE_LOGIN, { replace: true });
        } else {
            // Initial load uses skeleton (no backdrop overlay)
            actions.getWebAuthnRegistrationsAsync(
                authenticatedUser.authenticatedApp,
                authenticatedUser.authenticatedUser
            )
                .then(resp => setWebAuthnDevices(resp.data || []))
                .catch(resp => {
                    setWebAuthnDevices([]);
                    setAlert({ message: resp.message, severity: 'error' });
                });
        }
    }, []);

    const loadDevices = async () => {
        try {
            const resp = await actions.getWebAuthnRegistrationsAsync(
                authenticatedUser.authenticatedApp,
                authenticatedUser.authenticatedUser
            );
            setWebAuthnDevices(resp.data || []);
        } catch (resp) {
            setAlert({ message: resp.message, severity: 'error' });
        }
    };

    const deletePasskey = async () => {
        if (!deleteTarget) return;
        showLoader('Deleting passkey...');
        try {
            await actions.deleteWebAuthnRegistrationAsync(
                authenticatedUser.authenticatedApp,
                authenticatedUser.authenticatedUser,
                deleteTarget
            );
            setDeleteTarget(null);
            await loadDevices();
        } catch (resp) {
            setDeleteTarget(null);
            setAlert({ message: resp.message, severity: 'error' });
        } finally {
            hideLoader();
        }
    };

    return (
        <PageContainer id="pageWebAuthnDevices" dataAutomationId="page_web_authn_devices">
            <MuiDialog
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="webauthn-delete-title"
                aria-describedby="webauthn-delete-desc"
            >
                <DialogTitleWithClose id="webauthn-delete-title" onClose={() => setDeleteTarget(null)} dataAutomationId="webauthn_delete_title">{getLabelFromJspVariables('title_confirm_delete')}</DialogTitleWithClose>
                <DialogContent>
                    <p id="webauthn-delete-desc">{getLabelFromJspVariables('question_delete_passkey', deleteTarget?.deviceName)}</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => setDeleteTarget(null)}
                        data-automation-id="webauthn_delete_cancel_btn">{getLabelFromJspVariables('action_cancel')}</Button>
                    <Button variant="contained" onClick={deletePasskey}
                        data-automation-id="webauthn_delete_confirm_btn">{getLabelFromJspVariables('action_delete')}</Button>
                </DialogActions>
            </MuiDialog>
            <Box sx={panelStyle}>
                <UserMenu
                    authenticatedUser={authenticatedUser}
                    onLogout={() => logout(authenticatedUser?.user)}
                    onNavigate={(route) => navigate(route)}
                />
                <IceIcon launcher={launcher} />
                <PageHeader dataAutomationId="webauthn_devices_header">{getLabelFromJspVariables('title_registered_passkeys')}</PageHeader>
                <h4 style={{ textTransform: 'unset' }}>
                    (Yubikeys / Fingerprints)
                </h4>
                {alert && <Alert severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                <Box id="divSetupNewDevice" data-automation-id="div_setup_new_device" role="region" aria-label="Registered passkeys">
                        <Table sx={{ '& td, & th': { borderTop: 0 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>{getLabelFromJspVariables('label_passkey_name_col')}</TableCell>
                                    <TableCell>{getLabelFromJspVariables('label_passkey_reg_date')}</TableCell>
                                    <TableCell>{getLabelFromJspVariables('label_delete_passkey')}</TableCell>
                                </TableRow>
                            </TableHead>
                            {webAuthnDevices ?
                                <TableBody>
                                    {webAuthnDevices.map((row, i) => {
                                        if (row) {
                                            return (
                                                <TableRow key={i}>
                                                    <TableCell></TableCell>
                                                    <TableCell>{row.deviceName}</TableCell>
                                                    <TableCell>{(new Date(row.createdDate)).toLocaleDateString()}</TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            aria-label={`Delete passkey ${row.deviceName}`}
                                                            size="small"
                                                            onClick={() => setDeleteTarget(row)}
                                                            data-automation-id={`webauthn_devices_delete_${i}_btn`}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }
                                        return null;
                                    })}
                                </TableBody>
                                :
                                <PasskeyTableSkeleton />
                            }
                        </Table>
                </Box>
                <Tooltip id="registerPasskeyTooltip" data-automation-id="register_passkey_tooltip" title={getLabelFromJspVariables('title_register_passkey')} placement="bottom">
                    <Button variant="contained" fullWidth onClick={toPageWebAuthnRegister}
                        data-automation-id="webauthn_devices_register_btn">
                        {getLabelFromJspVariables('action_register_passkey')}
                    </Button>
                </Tooltip>
            </Box>
        </PageContainer>
    );
};

export default PageWebAuthnDevices;
